import React, { useState } from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';

import {
  Box,
  Button,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import InputField from 'components/Input-Field/input-field';
import FTIcons from 'assets/icons/FTIcons';
import { tokens } from 'theme/tokens';
import { Visibility, VisibilityOff } from '@mui/icons-material';

const PasswordResetForm = ({
  resetPassword,
  fnOnPasswordResetHandler
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const togglePasswordVisibility = () => setShowPassword((prev) => !prev);
  const toggleCurrentPasswordVisibility = () => setShowCurrentPassword((prev) => !prev);

  const passwordChecklist = [
    { label: 'At least 8 characters', test: (val) => val.length >= 8 },
    { label: 'One uppercase letter', test: (val) => /[A-Z]/.test(val) },
    { label: 'One lowercase letter', test: (val) => /[a-z]/.test(val) },
    { label: 'One number', test: (val) => /[0-9]/.test(val) },
    { label: 'One special character', test: (val) => /[^A-Za-z0-9]/.test(val) },
  ];
  const formik = useFormik({
    initialValues: { ...resetPassword },

    validationSchema: Yup.object().shape({
      currentPassword: Yup.string().required('Please enter your current password.'),
      password: Yup.string().required('Please enter your password')
        .matches(
          /^.*(?=.{8,})((?=.*[!@#$%^&*()\-_=+{};:,<.>]){1})(?=.*\d)((?=.*[a-z]){1})((?=.*[A-Z]){1}).*$/,
          'Password must contain at least 8 characters, one uppercase, one number and one special case character'
        ),
      confirmPassword: Yup.string().required('Please confirm your password')
        .when('password', {
          is: password => (password && password.length > 0 ? true : false),
          then: Yup.string().oneOf([Yup.ref('password')], 'Password doesn\'t match')
        }),
    }),

    onSubmit: values => {
      fnOnPasswordResetHandler(values);
    },
  });

  return (
    <form onSubmit={formik.handleSubmit}>
      <Grid container spacing={2}>
        <Grid size={{ xs: 6, sm: 4 }}>
          <InputField
            error={Boolean(formik.touched.currentPassword && formik.errors.currentPassword)}
            helperText={formik.touched.currentPassword && formik.errors.currentPassword}
            fullWidth
            label='Current Password'
            margin='normal'
            name='currentPassword'
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
            type='password'
            value={formik.values.currentPassword}
            variant='outlined'
            slotProps={{
              input: {
                endAdornment: <InputAdornment position="end">
                  <IconButton
                    onClick={toggleCurrentPasswordVisibility}
                    edge="end"
                  >
                    {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              }
            }}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 4 }}>
          <InputField
            error={Boolean(formik.touched.password && formik.errors.password)}
            helperText={formik.touched.password && formik.errors.password}
            fullWidth
            label='Password'
            margin='normal'
            name='password'
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
            type='password'
            value={formik.values.password}
            variant='outlined'
            slotProps={{
              input: {
                endAdornment: <InputAdornment position="end">
                  <IconButton
                    onClick={togglePasswordVisibility}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              }
            }}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 4 }}>
          <InputField
            error={Boolean(formik.touched.confirmPassword && formik.errors.confirmPassword)}
            fullWidth
            helperText={formik.touched.confirmPassword && formik.errors.confirmPassword}
            label='Confirm Password'
            margin='normal'
            name='confirmPassword'
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
            type='password'
            value={formik.values.confirmPassword}
            variant='outlined'
            slotProps={{
              input: {
                endAdornment: <InputAdornment position="end">
                  <IconButton
                    onClick={togglePasswordVisibility}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              }
            }}
          />
        </Grid>
        <Grid xs={12}>
          <Stack direction={'row'} spacing={2}>
            {passwordChecklist.map(({ label, test }) => (
              <Box display={'flex'} gap={.5}>
                <Typography>{test(formik.values.password) ?
                  <FTIcons.CheckCircleIcon width={20} height={20} fill={colors.success.main} /> :
                  <FTIcons.CrossCircleIcon width={20} height={20} fill={colors.error.main} />}
                </Typography>
                <Typography variant='body1'>{label}</Typography>
              </Box>
            ))}
          </Stack>
        </Grid>
      </Grid>
      <Box
        display='flex'
        justifyContent='flex-end'
        p={2}>

        <Button
          type='submit'
          variant='contained'
          color='primary'
        >
          Reset Password
        </Button>
      </Box>
    </form>
  );
};

export default PasswordResetForm;